import { endOfMonth, set, startOfMonth } from "date-fns";
import { Button, TextBox } from "devextreme-react";
import {
  Column,
  DataGrid,
  FilterRow,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import { DateRangeBox } from "devextreme-react/date-range-box";
import { LoadPanel } from "devextreme-react/load-panel";
import { SelectBox } from "devextreme-react/select-box";
import { exportDataGrid } from "devextreme/excel_exporter";
import notify from "../../lib/notify";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSummaryOfPresencesHoursInProject } from "../../api/presence";
import {
  exportSummaryOfHoursByProjectsToXlsx,
  exportSummaryOfHoursByProjectsSingleSheetToXlsx,
} from "../presence/api/Presence";
import { getProjectsAvailableForUser } from "../../api/projects";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import {
  Project,
  RoleEnum,
  SummaryOfPresencesHoursInProjectEntry,
} from "../../types";
import { useScreenSize } from "../../utils/media-query";

const getCompanyName = (id: number) => {
  switch (id) {
    case 1:
      return "Invenio";
    case 2:
      return "Invenio QD";
    case 3:
      return "Quality & Human Services";
    case 4:
      return "IPSUPPORT";
    default:
      return "";
  }
};

export default function HourlySummaryByProject() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number>();
  const [startDate, setStartDate] = useState<Date>(() =>
    set(startOfMonth(new Date()), {
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
  );
  const [endDate, setEndDate] = useState<Date>(() =>
    set(endOfMonth(new Date()), {
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
  );
  const [entries, setEntries] = useState<
    SummaryOfPresencesHoursInProjectEntry[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [mobileSearch, setMobileSearch] = useState("");

  const filteredEntries = useMemo(() => {
    const q = mobileSearch.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.lastName, e.firstName, e.code, getCompanyName(e.companyId)]
        .filter(Boolean)
        .some((v) => v!.toString().toLowerCase().includes(q)),
    );
  }, [entries, mobileSearch]);

  const exportMobileToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(
        t("hourlySummary.views.accordingToProjects.excel.sheetName"),
      );
      worksheet.columns = [
        {
          header: t(
            "hourlySummary.views.accordingToProjects.dataGrid.columns.company",
          ),
          key: "company",
          width: 20,
        },
        {
          header: t(
            "hourlySummary.views.accordingToProjects.dataGrid.columns.code",
          ),
          key: "code",
          width: 18,
        },
        {
          header: t(
            "hourlySummary.views.accordingToProjects.dataGrid.columns.lastName",
          ),
          key: "lastName",
          width: 20,
        },
        {
          header: t(
            "hourlySummary.views.accordingToProjects.dataGrid.columns.firstName",
          ),
          key: "firstName",
          width: 20,
        },
        {
          header: t(
            "hourlySummary.views.accordingToProjects.dataGrid.columns.hours",
          ),
          key: "hours",
          width: 10,
        },
      ];
      filteredEntries.forEach((e) => {
        worksheet.addRow({
          company: getCompanyName(e.companyId),
          code: e.code,
          lastName: e.lastName,
          firstName: e.firstName,
          hours: e.hours,
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `WedlugProjektow_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const exportAllProjectsToExcel = async () => {
    try {
      setIsLoading(true);
      const effectiveCompanyId =
        user?.roles?.includes(RoleEnum.AdministratorGlowny) && companyId !== 0
          ? companyId
          : undefined;

      await exportSummaryOfHoursByProjectsToXlsx(
        startDate,
        endDate,
        effectiveCompanyId,
      );
    } catch (err: any) {
      notify(
        `${t("Error:")} ${err?.message || err || t("Unknown error")}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const exportAllProjectsSingleSheetToExcel = async () => {
    try {
      setIsLoading(true);
      const effectiveCompanyId =
        user?.roles?.includes(RoleEnum.AdministratorGlowny) && companyId !== 0
          ? companyId
          : undefined;

      await exportSummaryOfHoursByProjectsSingleSheetToXlsx(
        startDate,
        endDate,
        effectiveCompanyId,
      );
    } catch (err: any) {
      notify(
        `${t("Error:")} ${err?.message || err || t("Unknown error")}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = useCallback(() => {
    if (!user?.employeeId || !user?.companyId) return;

    setIsLoading(true);
    setProjects([]);
    setProjectId(undefined);
    setEntries([]);

    getProjectsAvailableForUser(
      !user?.roles?.includes(RoleEnum.AdministratorGlowny) || companyId === 0
        ? undefined
        : companyId,
    )
      .then((projects) => {
        setProjects(projects);

        if (projects.length > 0) {
          setProjectId(projects[0].id);
        } else {
          setProjectId(undefined);
        }
      })
      .catch((error) => notify(error.message || error, "error", 5000))
      .finally(() => setIsLoading(false));
  }, [companyId, user?.companyId, user?.employeeId, user?.roles]);

  const fetchSummaryOfPresencesHoursInProject = useCallback(
    (projectId: number, startDate: Date, endDate: Date) => {
      setIsLoading(true);
      const effectiveCompanyId =
        user?.roles?.includes(RoleEnum.AdministratorGlowny) && companyId !== 0
          ? companyId
          : undefined;
      getSummaryOfPresencesHoursInProject(
        projectId,
        startDate,
        endDate,
        effectiveCompanyId,
      )
        .then(setEntries)
        .catch((error) => notify(error, "error", 5000))
        .finally(() => setIsLoading(false));
    },
    [companyId, user?.roles],
  );

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (projectId) {
      fetchSummaryOfPresencesHoursInProject(projectId, startDate, endDate);
    }
  }, [projectId, startDate, endDate, fetchSummaryOfPresencesHoursInProject]);

  if (isLoading) {
    return <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />;
  }

  if (isMobile) {
    return (
      <div className="flex flex-col gap-2">
        <div className="sticky top-0 z-10 flex flex-col gap-2 py-2 bg-white dark:bg-[#1e1e1e]">
          <SelectBox
            dataSource={projects}
            value={projectId}
            valueExpr="id"
            displayExpr="acronym"
            labelMode="static"
            label={t(
              "hourlySummary.views.accordingToProjects.dataGrid.toolbar.project",
            )}
            searchEnabled
            onValueChange={setProjectId}
            width="100%"
          />
          <DateRangeBox
            startDate={startDate}
            endDate={endDate}
            labelMode="static"
            startDateLabel={t(
              "hourlySummary.views.accordingToProjects.dataGrid.toolbar.startDate",
            )}
            endDateLabel={t(
              "hourlySummary.views.accordingToProjects.dataGrid.toolbar.endDate",
            )}
            applyValueMode="useButtons"
            displayFormat="dd.MM.yyyy"
            onStartDateChange={(value) => {
              if (value instanceof Date) setStartDate(value);
            }}
            onEndDateChange={(value) => {
              if (value instanceof Date) setEndDate(value);
            }}
            width="100%"
          />
          <TextBox
            value={mobileSearch}
            onValueChanged={(e) => setMobileSearch(e.value || "")}
            placeholder={t("searchPlaceholder")}
            showClearButton
            width="100%"
            mode="search"
          />
          <Button
            icon="exportxlsx"
            text={t("Export all")}
            type="success"
            stylingMode="contained"
            width="100%"
            onClick={exportMobileToExcel}
            disabled={filteredEntries.length === 0}
          />
          <Button
            icon="exportxlsx"
            text="Eksportuj wszystkie projekty"
            type="default"
            stylingMode="contained"
            width="100%"
            onClick={exportAllProjectsToExcel}
          />
          <Button
            icon="detailslayout"
            text="Wszystkie projekty – jedna tabela (projekt + typ umowy)"
            type="default"
            stylingMode="contained"
            width="100%"
            onClick={exportAllProjectsSingleSheetToExcel}
          />
        </div>
        <div className="flex flex-col gap-3 pb-4">
          {filteredEntries.length === 0 ? (
            <div
              className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
              role="alert"
            >
              {t("noSubordinateEmployees")}
            </div>
          ) : (
            filteredEntries.map((row, idx) => (
              <div
                key={`${row.companyId}-${row.code}-${idx}`}
                className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1e1e1e] shadow-sm overflow-hidden"
              >
                <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2 border-b border-black/5 dark:border-white/5">
                  <div className="min-w-0">
                    <div className="text-base font-bold break-words">
                      {`${row.lastName} ${row.firstName}`}
                    </div>
                    <div className="text-xs opacity-70">
                      {getCompanyName(row.companyId)}
                      {row.code ? ` · ${row.code}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] uppercase opacity-70">
                      {t(
                        "hourlySummary.views.accordingToProjects.dataGrid.columns.hours",
                      )}
                    </div>
                    <div className="text-lg font-bold tabular-nums">
                      {typeof row.hours === "number"
                        ? row.hours.toFixed(2)
                        : row.hours}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <DataGrid
      dataSource={entries}
      remoteOperations
      showBorders
      allowColumnResizing
      allowColumnReordering
      searchPanel={{
        visible: true,
        placeholder: t("searchPlaceholder"),
      }}
      paging={{
        pageSize: 10,
      }}
      pager={{
        visible: true,
        showInfo: true,
        showNavigationButtons: true,
      }}
      columnAutoWidth
      export={{
        enabled: true,
        allowExportSelectedData: false,
        texts: {
          exportAll: t("Export all"),
          exportSelectedRows: t("Export selected"),
        },
      }}
      onExporting={async (e) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(
          t("hourlySummary.views.accordingToProjects.excel.sheetName"),
        );

        await exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          `WedlugProjektow_${new Date().toISOString().split("T")[0]}.xlsx`,
        );

        e.cancel = true;
      }}
    >
      <Toolbar>
        <Item
          name="searchPanel"
          location="before"
          locateInMenu="auto"
          cssClass="datagrid-search-panel-without-margin-left"
        />
        <Item name="exportButton" location="after" visible={false} />
        <Item
          name="exportButton"
          location="after"
          locateInMenu="never"
          options={{
            icon: "xlsxfile",
            hint: "Eksportuj bieżący projekt do Excel",
            stylingMode: "contained",
            elementAttr: {
              class:
                "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-lg shadow-md transition duration-150 ease-in-out",
            },
          }}
        />
        <Item location="after" locateInMenu="never">
          <Button
            icon="exportxlsx"
            hint="Eksportuj wszystkie projekty (każdy jako osobna zakładka)"
            stylingMode="contained"
            onClick={exportAllProjectsToExcel}
            elementAttr={{
              class:
                "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg shadow-md transition duration-150 ease-in-out",
            }}
          />
        </Item>
        <Item location="after" locateInMenu="never">
          <Button
            icon="detailslayout"
            hint="Eksportuj wszystkie projekty do jednej tabeli (kolumny: projekt + typ umowy)"
            stylingMode="contained"
            onClick={exportAllProjectsSingleSheetToExcel}
            elementAttr={{
              class:
                "bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-lg shadow-md transition duration-150 ease-in-out",
            }}
          />
        </Item>
        <Item location="after" locateInMenu="auto">
          <SelectBox
            dataSource={projects}
            value={projectId}
            valueExpr="id"
            displayExpr="acronym"
            labelMode="static"
            label={t(
              "hourlySummary.views.accordingToProjects.dataGrid.toolbar.project",
            )}
            searchEnabled
            onValueChange={setProjectId}
            width={200}
          />
        </Item>
        <Item location="after" locateInMenu="auto">
          <DateRangeBox
            startDate={startDate}
            endDate={endDate}
            labelMode="static"
            startDateLabel={t(
              "hourlySummary.views.accordingToProjects.dataGrid.toolbar.startDate",
            )}
            endDateLabel={t(
              "hourlySummary.views.accordingToProjects.dataGrid.toolbar.endDate",
            )}
            applyValueMode="useButtons"
            displayFormat="dd.MM.yyyy"
            onStartDateChange={(value) => {
              if (value instanceof Date) {
                setStartDate(value);
              }
            }}
            onEndDateChange={(value) => {
              if (value instanceof Date) {
                setEndDate(value);
              }
            }}
            width={350}
          />
        </Item>
      </Toolbar>
      <FilterRow visible />
      <Column
        dataField="lastName"
        dataType="string"
        caption={t(
          "hourlySummary.views.accordingToProjects.dataGrid.columns.lastName",
        )}
        width={250}
        fixed
        fixedPosition="left"
      />
      <Column
        dataField="firstName"
        dataType="string"
        caption={t(
          "hourlySummary.views.accordingToProjects.dataGrid.columns.firstName",
        )}
        width={200}
        fixed
        fixedPosition="left"
      />
      <Column
        dataField="companyId"
        dataType="string"
        caption={t(
          "hourlySummary.views.accordingToProjects.dataGrid.columns.company",
        )}
        customizeText={({ value }) => {
          switch (value) {
            case 1:
              return "Invenio";
            case 2:
              return "Invenio QD";
            case 3:
              return "Quality & Human Services";
            case 4:
              return "IPSUPPORT";
            default:
              return "";
          }
        }}
        width={200}
        fixed
        fixedPosition="left"
      />
      <Column
        dataField="code"
        dataType="string"
        caption={t(
          "hourlySummary.views.accordingToProjects.dataGrid.columns.code",
        )}
        width={300}
      />
      <Column
        dataField="hours"
        dataType="number"
        caption={t(
          "hourlySummary.views.accordingToProjects.dataGrid.columns.hours",
        )}
        alignment="left"
        format={{
          type: "decimal",
        }}
      />
    </DataGrid>
  );
}
